import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiMessageCircle, FiCalendar, FiCheckCircle, FiAlertCircle, FiShield, FiAward, FiUsers, FiChevronDown, FiHeart, FiStar, FiLock, FiTrendingUp, FiVideo, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import { submitContactForm } from '../services/dataService';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    primaryConcern: 'Chiropractic Adjustment',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!formData.name.trim() || !formData.phone.trim()) {
        throw new Error('Please fill in all required fields.');
      }

      const result = await submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim() || 'noemail@provided.com',
        phone: formData.phone.trim(),
        message: formData.message.trim() || `Primary Concern: ${formData.primaryConcern}`,
        primaryConcern: formData.primaryConcern
      });

      setSubmitStatus('success');
      setSubmitMessage(result.message);
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        primaryConcern: 'Chiropractic Adjustment',
        message: ''
      });

    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(error.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: FiPhone,
      title: 'Call Us Directly',
      subtitle: 'Instant Connection',
      description: 'Talk to our care team now',
      action: 'Call +91 95815 56660',
      href: 'tel:+919581556660',
      color: 'emerald',
      availability: 'Mon-Sun: 10 AM - 9 PM'
    },
    {
      icon: FiMessageCircle,
      title: 'WhatsApp Chat',
      subtitle: 'Quick & Convenient',
      description: 'Get instant responses on WhatsApp',
      action: 'Start Chat →',
      href: 'https://wa.me/919581556660',
      color: 'green',
      availability: 'Response in minutes'
    },
    {
      icon: FiVideo,
      title: 'Video Consultation',
      subtitle: 'From Anywhere',
      description: 'Consult from home via video call',
      action: 'Book Video Call',
      href: 'tel:+919581556660',
      color: 'blue',
      availability: 'Book your slot now'
    }
  ];

  const faqs = [
    {
      question: 'How do I book my first consultation?',
      answer: 'Simply fill out the form above, call us, or WhatsApp us. We\'ll confirm your slot within 2 hours during working hours.'
    },
    {
      question: 'Is the consultation free?',
      answer: 'Yes! Your first consultation with our expert is complimentary. We\'ll assess your condition and recommend the best treatment plan.'
    },
    {
      question: 'Do you accept walk-ins?',
      answer: 'We prioritize appointments to ensure quality care. However, emergency cases are handled immediately. Please call ahead when possible.'
    },
    {
      question: 'What should I bring to my appointment?',
      answer: 'Please bring any medical reports, scans (X-ray/MRI), and a list of current medications. Wear comfortable clothing.'
    },
    {
      question: 'Is my information secure?',
      answer: 'Absolutely. We follow strict HIPAA-compliant privacy standards. Your personal and medical information is encrypted and never shared with third parties.'
    },
    {
      question: 'Do you provide treatment reports?',
      answer: 'Yes, comprehensive treatment reports and progress tracking are provided to all patients. You can access them anytime through our portal.'
    }
  ];

  return (
    <div className="bg-white">
      <SEO
        pageType="contact"
        title="Contact JIVA NADI | Book Your Consultation | Mumbai Chiropractor"
        description="Get in touch with Dr. Saurabh Singh for expert chiropractic and acupuncture treatments. Book your consultation today for natural pain relief."
        keywords="contact JIVA NADI, book appointment, chiropractor Mumbai, consultation booking, pain relief treatment"
      />

      {/* ===== PREMIUM HERO SECTION ===== */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50/40 pt-24 pb-32 lg:pt-5 lg:pb-40 overflow-hidden">
        {/* Floating Orbs Background */}
        <div className="absolute top-20 right-10 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl animate-pulse delay-700" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-emerald-100/20 to-teal-100/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Live Status Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center space-x-3 bg-white/90 backdrop-blur-md border border-emerald-200/50 rounded-full px-5 py-2.5 shadow-lg"
              >
                <div className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </div>
                <span className="text-sm font-bold text-gray-900">We're Available Now</span>
                <span className="text-xs text-gray-500 hidden sm:inline">• Response in 2 hours</span>
              </motion.div>

              {/* Main Headline */}
              <div className="space-y-6">
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.1] tracking-tight">
                  Begin Your
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                    Healing Journey
                  </span>
                </h1>
                <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-xl font-light">
                  Connect with India's leading experts in non-surgical pain relief. 
                  <span className="font-semibold text-gray-900"> Your first consultation is complimentary.</span>
                </p>
              </div>

              {/* Trust Indicators with Icons */}
              {/* <div className="grid grid-cols-3 gap-6 pt-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-center space-y-2"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiAward} className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-black text-gray-900">15+</div>
                  <p className="text-sm text-gray-600 font-medium">Years Expert Care</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-center space-y-2"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiHeart} className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-black text-gray-900">30K+</div>
                  <p className="text-sm text-gray-600 font-medium">Happy Patients</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-center space-y-2"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiStar} className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-black text-gray-900">4.9★</div>
                  <p className="text-sm text-gray-600 font-medium">Patient Rating</p>
                </motion.div>
              </div> */}

              {/* Primary CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap gap-4 pt-4"
              >
                <a
                  href="tel:+919581556660"
                  className="group inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>Call Now</span>
                </a>
                <a
                  href="https://wa.me/919581556660"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center space-x-3 bg-white hover:bg-green-50 text-gray-900 px-8 py-4 rounded-2xl font-bold text-lg border-2 border-gray-200 hover:border-green-500 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-green-600 group-hover:scale-110 transition-transform" />
                  <span>WhatsApp Us</span>
                </a>
              </motion.div>

              {/* Security Badge */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="flex items-center space-x-2 text-sm text-gray-500 pt-2"
              >
                <SafeIcon icon={FiLock} className="w-4 h-4 text-emerald-600" />
                <span>Your information is 100% secure and confidential</span>
              </motion.div>
            </motion.div>

            {/* Right: Hero Image Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="relative"
            >
              {/* Main Image Card */}
              <div className="relative rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&h=700&fit=crop"
                  alt="JIVA NADI Wellness Center"
                  className="w-full h-[450px] lg:h-[550px] object-cover"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 via-transparent to-transparent" />
                
                {/* Floating Stats Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-gray-100"
                >
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Next Available</p>
                      <p className="text-xl font-black text-gray-900">Today, 3:00 PM</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">First Visit</p>
                      <p className="text-xl font-black text-emerald-600">FREE</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Floating Badge - Success Rate */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
                className="absolute -left-6 top-20 bg-white rounded-2xl px-6 py-4 shadow-2xl border border-gray-100"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-gray-900">97%</p>
                    <p className="text-xs text-gray-500 font-semibold">Success Rate</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Badge - No Surgery */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.6 }}
                className="absolute -right-6 top-40 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl px-6 py-4 shadow-2xl"
              >
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-white" />
                  <p className="text-sm font-bold text-white">100% Non-Surgical</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== 3 CONTACT METHODS - PREMIUM CARDS ===== */}
      <section className="py-20 lg:py-28 -mt-20 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
              Choose Your <span className="text-emerald-600">Preferred</span> Way to Connect
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible options to reach us instantly. No waiting, no hassle—just caring support when you need it.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {contactMethods.map((method, index) => (
              <motion.a
                key={index}
                href={method.href}
                target={method.href.startsWith('http') ? '_blank' : undefined}
                rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`group relative bg-white rounded-3xl p-8 lg:p-10 shadow-xl border-2 ${
                  method.color === 'emerald' ? 'border-emerald-100 hover:border-emerald-300 hover:shadow-emerald-500/20' :
                  method.color === 'green' ? 'border-green-100 hover:border-green-300 hover:shadow-green-500/20' :
                  'border-blue-100 hover:border-blue-300 hover:shadow-blue-500/20'
                } hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden`}
              >
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${
                  method.color === 'emerald' ? 'from-emerald-50 to-teal-50' :
                  method.color === 'green' ? 'from-green-50 to-emerald-50' :
                  'from-blue-50 to-cyan-50'
                }`} />

                <div className="relative space-y-6">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                    method.color === 'emerald' ? 'bg-emerald-100 group-hover:bg-emerald-600' :
                    method.color === 'green' ? 'bg-green-100 group-hover:bg-green-600' :
                    'bg-blue-100 group-hover:bg-blue-600'
                  } transition-all duration-500`}>
                    <SafeIcon 
                      icon={method.icon} 
                      className={`w-8 h-8 ${
                        method.color === 'emerald' ? 'text-emerald-600 group-hover:text-white' :
                        method.color === 'green' ? 'text-green-600 group-hover:text-white' :
                        'text-blue-600 group-hover:text-white'
                      } transition-colors duration-500`}
                    />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">{method.subtitle}</p>
                    <h3 className="text-2xl lg:text-3xl font-black text-gray-900">{method.title}</h3>
                    <p className="text-gray-600 text-base leading-relaxed">{method.description}</p>
                  </div>

                  {/* Availability Badge */}
                  <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${
                    method.color === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
                    method.color === 'green' ? 'bg-green-50 text-green-700' :
                    'bg-blue-50 text-blue-700'
                  } text-sm font-semibold`}>
                    <SafeIcon icon={FiClock} className="w-4 h-4" />
                    <span>{method.availability}</span>
                  </div>

                  {/* CTA */}
                  <div className={`inline-flex items-center space-x-2 font-bold text-lg ${
                    method.color === 'emerald' ? 'text-emerald-600 group-hover:text-emerald-700' :
                    method.color === 'green' ? 'text-green-600 group-hover:text-green-700' :
                    'text-blue-600 group-hover:text-blue-700'
                  } transition-colors pt-2`}>
                    <span>{method.action}</span>
                    <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MAIN CONTACT FORM SECTION ===== */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 via-white to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 lg:mb-20"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-emerald-100 rounded-full px-5 py-2 mb-6"
            >
              <SafeIcon icon={FiCalendar} className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-bold text-emerald-700">Book Your Free Consultation</span>
            </motion.div>
            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-6">
              Let's Start Your <span className="text-emerald-600">Recovery</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Fill out this quick form and our care team will reach out within 2 hours to schedule your complimentary consultation.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            
            {/* Main Form - Takes 3 columns */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-[2.5rem] p-8 lg:p-12 shadow-2xl border border-gray-100">
                {/* Form Header */}
                <div className="mb-8 pb-8 border-b border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCalendar} className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-black text-gray-900 mb-2">
                        Request Appointment
                      </h3>
                      <p className="text-gray-600 text-base">
                        All fields marked with * are required. We respect your privacy.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Status Messages */}
                  {submitStatus && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-5 rounded-2xl flex items-start space-x-3 ${
                        submitStatus === 'success'
                          ? 'bg-emerald-50 border-2 border-emerald-200'
                          : 'bg-red-50 border-2 border-red-200'
                      }`}
                    >
                      <SafeIcon
                        icon={submitStatus === 'success' ? FiCheckCircle : FiAlertCircle}
                        className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
                          submitStatus === 'success' ? 'text-emerald-600' : 'text-red-600'
                        }`}
                      />
                      <div>
                        <p className={`font-bold mb-1 ${
                          submitStatus === 'success' ? 'text-emerald-900' : 'text-red-900'
                        }`}>
                          {submitStatus === 'success' ? 'Success!' : 'Error'}
                        </p>
                        <p className={`text-sm ${
                          submitStatus === 'success' ? 'text-emerald-700' : 'text-red-700'
                        }`}>
                          {submitMessage}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 flex items-center space-x-1">
                        <span>Full Name</span>
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-5 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400 font-medium"
                        placeholder="Rahul Sharma"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 flex items-center space-x-1">
                        <span>Phone Number</span>
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-5 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400 font-medium"
                        placeholder="+91 98765 43210"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700">
                      Email Address <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-5 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400 font-medium"
                      placeholder="your.email@example.com"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 flex items-center space-x-1">
                      <span>Select Service</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="primaryConcern"
                      value={formData.primaryConcern}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-5 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all text-gray-900 font-medium appearance-none cursor-pointer"
                      disabled={isSubmitting}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2310b981'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                        backgroundSize: '1.5rem'
                      }}
                    >
                      <option>Chiropractic Adjustment</option>
                      <option>Spinal Pain (Spondylitis/Disc)</option>
                      <option>Neurological (Paralysis/Stroke)</option>
                      <option>Sports Injury</option>
                      <option>Nadi Pariksha Consultation</option>
                      <option>General Wellness Checkup</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700">
                      Your Message <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-5 py-4 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400 resize-none font-medium"
                      placeholder="Tell us about your symptoms or any questions you have..."
                      disabled={isSubmitting}
                    ></textarea>
                    <p className="text-xs text-gray-500 flex items-center space-x-1">
                      <SafeIcon icon={FiLock} className="w-3 h-3" />
                      <span>Your information is encrypted and kept confidential</span>
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 disabled:from-emerald-400 disabled:to-emerald-400 disabled:cursor-not-allowed text-white font-black text-lg py-5 rounded-2xl shadow-xl hover:shadow-2xl transition-all flex items-center justify-center space-x-3 group"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        <span>Sending Request...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Request</span>
                        <SafeIcon icon={FiSend} className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-center text-gray-500 flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiShield} className="w-4 h-4 text-emerald-600" />
                    <span>
                      By submitting, you agree to receive communications from JIVA NADI. Your information is secure and never shared.
                    </span>
                  </p>
                </form>
              </div>
            </motion.div>

            {/* Sidebar Benefits - Takes 2 columns */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Why Book With Us */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border border-emerald-100 shadow-lg">
                <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-emerald-600" />
                  <span>Why Book With Us?</span>
                </h3>
                <div className="space-y-4">
                  {[
                    { icon: FiShield, text: '100% Non-Surgical Treatment', color: 'emerald' },
                    { icon: FiAward, text: '15+ Years Expert Care', color: 'emerald' },
                    { icon: FiHeart, text: 'Personalized Treatment Plans', color: 'emerald' },
                    { icon: FiCheckCircle, text: 'No Medication Required', color: 'emerald' },
                    { icon: FiUsers, text: '30,000+ Happy Patients', color: 'emerald' },
                    { icon: FiStar, text: '4.9★ Patient Rating', color: 'emerald' }
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-3 bg-white rounded-xl p-4 shadow-sm border border-emerald-100/50"
                    >
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={benefit.icon} className="w-5 h-5 text-emerald-600" />
                      </div>
                      <span className="font-semibold text-gray-900">{benefit.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Quick Contact Options */}
              <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-xl font-black text-gray-900 mb-6">
                  Prefer to Talk Now?
                </h3>
                <div className="space-y-4">
                  <a
                    href="tel:+919581556660"
                    className="group flex items-center space-x-4 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 rounded-2xl p-5 transition-all border-2 border-emerald-200 hover:border-emerald-400"
                  >
                    <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiPhone} className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-600">Call Us Now</p>
                      <p className="text-lg font-bold text-gray-900">+91 95815 56660</p>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-emerald-600 group-hover:translate-x-1 transition-transform" />
                  </a>

                  <a
                    href="https://wa.me/919581556660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center space-x-4 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-2xl p-5 transition-all border-2 border-green-200 hover:border-green-400"
                  >
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiMessageCircle} className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-600">WhatsApp Chat</p>
                      <p className="text-lg font-bold text-gray-900">Quick Response</p>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>

              {/* Emergency Note */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-900 mb-1">Medical Emergency?</p>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      For urgent cases, please call us directly. We prioritize emergency consultations and ensure immediate attention.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TRUST & REASSURANCE BADGES ===== */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
              Why 30,000+ Indians <span className="text-emerald-600">Trust Us</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our commitment to natural, effective, and safe healing has made us a trusted name in healthcare
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-6 lg:p-8 shadow-lg border border-emerald-100 text-center space-y-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FiShield} className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-black text-xl text-gray-900 mb-2">No Surgery</h3>
                <p className="text-sm text-gray-600 leading-relaxed">100% Non-Invasive Treatments</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-6 lg:p-8 shadow-lg border border-emerald-100 text-center space-y-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-black text-xl text-gray-900 mb-2">No Medicine</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Natural Healing Methods</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-3xl p-6 lg:p-8 shadow-lg border border-emerald-100 text-center space-y-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-black text-xl text-gray-900 mb-2">Certified</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Licensed Expert Practitioners</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-3xl p-6 lg:p-8 shadow-lg border border-emerald-100 text-center space-y-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FiUsers} className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="font-black text-xl text-gray-900 mb-2">Trusted</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Across 5 Indian States</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== LOCATION & WORKING HOURS ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
              Visit Us at Our <span className="text-emerald-600">Premium</span> Center
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience world-class care in our state-of-the-art wellness facility
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Location Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-[2.5rem] p-8 lg:p-10 shadow-xl border border-gray-100">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-gray-900 mb-2">Our Location</h3>
                    <p className="text-gray-600">Easy to find, convenient to reach</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                    <h4 className="font-bold text-gray-900 mb-3 text-lg">JIVA NADI Therapy Center</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      123, Wellness Plaza, Andheri West<br />
                      Mumbai, Maharashtra 400053<br />
                      India
                    </p>
                    <a
                      href="https://maps.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-emerald-600 hover:text-emerald-700 font-bold group transition-colors"
                    >
                      <span>Get Directions</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                      <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide mb-1">Parking</p>
                      <p className="text-sm font-bold text-gray-900">Available</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                      <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide mb-1">Metro</p>
                      <p className="text-sm font-bold text-gray-900">5 Min Walk</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Embedded Map */}
              <div className="rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.8267653730814!2d72.8347107!3d19.073836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA0JzI1LjgiTiA3MsKwNTAnMDUuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="JIVA NADI Location"
                ></iframe>
              </div>
            </motion.div>

            {/* Working Hours Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50 rounded-[2.5rem] p-8 lg:p-10 shadow-xl border border-emerald-100">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiClock} className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-gray-900 mb-2">Working Hours</h3>
                    <p className="text-gray-600">We're here when you need us</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-5 flex justify-between items-center border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                    <span className="font-bold text-gray-900">Monday - Friday</span>
                    <span className="text-emerald-600 font-black text-lg">10:00 AM - 9:00 PM</span>
                  </div>
                  <div className="bg-white rounded-2xl p-5 flex justify-between items-center border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                    <span className="font-bold text-gray-900">Saturday</span>
                    <span className="text-emerald-600 font-black text-lg">10:00 AM - 9:00 PM</span>
                  </div>
                  <div className="bg-white rounded-2xl p-5 flex justify-between items-center border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                    <span className="font-bold text-gray-900">Sunday</span>
                    <span className="text-emerald-600 font-black text-lg">10:00 AM - 6:00 PM</span>
                  </div>
                  <div className="bg-white rounded-2xl p-5 flex justify-between items-center border border-emerald-100 shadow-sm">
                    <span className="font-bold text-gray-900">Public Holidays</span>
                    <span className="text-gray-500 font-bold">By Appointment</span>
                  </div>
                </div>

                <div className="mt-8 bg-white rounded-2xl p-6 border border-emerald-200 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCalendar} className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-gray-900 mb-2">Appointment Recommended</p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Walk-ins are welcome, but booking ensures zero waiting time and personalized attention from our experts.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-[2.5rem] p-8 shadow-2xl text-white">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiPhone} className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black mb-2">24/7 Emergency Line</h3>
                    <p className="text-amber-100">For urgent cases outside working hours</p>
                  </div>
                </div>
                <a 
                  href="tel:+919581556660" 
                  className="inline-flex items-center space-x-3 bg-white text-gray-900 px-8 py-4 rounded-2xl font-black text-xl hover:bg-amber-50 transition-all shadow-lg hover:shadow-xl group"
                >
                  <span>+91 95815 56660</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default ContactPage;